import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingCart, Minus, Plus, Shield, Truck, RefreshCw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      toast.error("Product not found");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/cart/add`, {
        product_id: id,
        quantity
      });
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate("/cart");
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-12 animate-pulse">
            <div className="bg-[#121212] aspect-square" />
            <div className="space-y-4">
              <div className="bg-[#121212] h-8 w-1/3" />
              <div className="bg-[#121212] h-12 w-2/3" />
              <div className="bg-[#121212] h-24 w-full" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) return null;

  const accentColor = product.metal_type === "silver" ? "#C0C0C0" : "#D4AF37";

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="product-detail-page">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-[#0a0a0a] border border-[rgba(255,255,255,0.05)] overflow-hidden">
              <img 
                src={product.image_url}
                alt={product.name}
                className="w-full aspect-square object-cover"
                data-testid="product-image"
              />
            </div>
            
            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="text-center p-4 bg-[#121212] border border-[rgba(255,255,255,0.05)]">
                <Shield className="w-5 h-5 mx-auto mb-2" style={{ color: accentColor }} />
                <p className="text-xs text-[#737373]">BIS Hallmarked</p>
              </div>
              <div className="text-center p-4 bg-[#121212] border border-[rgba(255,255,255,0.05)]">
                <Truck className="w-5 h-5 mx-auto mb-2" style={{ color: accentColor }} />
                <p className="text-xs text-[#737373]">Free Shipping</p>
              </div>
              <div className="text-center p-4 bg-[#121212] border border-[rgba(255,255,255,0.05)]">
                <RefreshCw className="w-5 h-5 mx-auto mb-2" style={{ color: accentColor }} />
                <p className="text-xs text-[#737373]">Live Pricing</p>
              </div>
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div>
              <span className={`badge-${product.metal_type} inline-block mb-3`} data-testid="product-purity">
                {product.purity} {product.metal_type.toUpperCase()}
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-[#A3A3A3]" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Price Breakdown */}
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.05)] p-6" data-testid="price-breakdown">
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                Price Breakdown
              </h3>
              
              <div className="billing-row">
                <span className="text-[#A3A3A3]">Subtotal (Live Metal Value)</span>
                <span className="text-white">₹{(product.live_price * quantity).toLocaleString()}</span>
              </div>
              <div className="billing-row">
                <span className="text-[#A3A3A3]">Making Charges & GST</span>
                <span className="text-white">₹{(product.making_charges_gst * quantity).toLocaleString()}</span>
              </div>
              <div className="billing-row">
                <span className="text-[#A3A3A3]">Shipping</span>
                <span className="text-[#00E676]">FREE</span>
              </div>
              <div className="billing-row total">
                <span className="text-white">Total</span>
                <span style={{ color: accentColor }} className="text-3xl font-light tracking-tighter" data-testid="total-price">
                  ₹{(product.total_price * quantity).toLocaleString()}
                </span>
              </div>
              
              <p className="text-xs text-[#737373] mt-4">
                * Live rate: ₹{product.base_metal_rate}/g • Updated every 2 mins
              </p>
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4" data-testid="quantity-selector">
              <span className="text-sm text-[#A3A3A3]">Quantity:</span>
              <div className="flex items-center gap-2 bg-[#121212] border border-[rgba(255,255,255,0.1)] p-1">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                  data-testid="qty-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-white font-medium" data-testid="qty-value">
                  {quantity}
                </span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-2 hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                  data-testid="qty-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleAddToCart}
                disabled={adding}
                className="flex-1 btn-outline flex items-center justify-center gap-2"
                data-testid="add-to-cart-btn"
              >
                {adding ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" />
                    Add to Cart
                  </>
                )}
              </Button>
              <Button
                onClick={handleBuyNow}
                disabled={adding}
                className={`flex-1 flex items-center justify-center gap-2 ${product.metal_type === 'silver' ? 'btn-silver' : 'btn-gold'}`}
                data-testid="buy-now-btn"
              >
                Buy Now
              </Button>
            </div>

            {/* Features */}
            <div className="space-y-3 pt-6 border-t border-[rgba(255,255,255,0.05)]">
              <div className="flex items-center gap-3">
                <Check className="w-4 h-4" style={{ color: accentColor }} />
                <span className="text-sm text-[#A3A3A3]">BIS Hallmarked - Certified purity guaranteed</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-4 h-4" style={{ color: accentColor }} />
                <span className="text-sm text-[#A3A3A3]">Free insured shipping across India</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-4 h-4" style={{ color: accentColor }} />
                <span className="text-sm text-[#A3A3A3]">KYC required at checkout (Aadhaar/PAN)</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-4 h-4" style={{ color: accentColor }} />
                <span className="text-sm text-[#A3A3A3]">Prices locked at time of order</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;
